import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';

import { ROUTES } from '@/config/routes';

const EmailWarmupList = lazy(() =>
  import('./routes/EmailWarmupList').then((m) => ({ default: m.EmailWarmupList })),
);

// Its own chunk, and worth one: most visits to the list never open a mailbox, and the
// detail screen carries the history table and the health panel with it.
const EmailWarmupDetail = lazy(() =>
  import('./routes/EmailWarmupDetail').then((m) => ({ default: m.EmailWarmupDetail })),
);

// Its own chunk too: the form carries react-hook-form, its resolver and a zod schema that
// the two read screens have no use for.
const EmailWarmupCreate = lazy(() =>
  import('./routes/EmailWarmupCreate').then((m) => ({ default: m.EmailWarmupCreate })),
);

// The plans: its own chunk, since it carries the whole pricing page and most visits to the list never open it.
const EmailWarmupBillingPage = lazy(() =>
  import('./routes/EmailWarmupBillingPage').then((m) => ({ default: m.EmailWarmupBillingPage })),
);

// The operator's receiver pool: its own chunk, and one almost nobody downloads.
const ReceiversPage = lazy(() =>
  import('./routes/ReceiversPage').then((m) => ({ default: m.ReceiversPage })),
);

/**
 * The Email Warmup screens.
 *
 * <p>Lazy, like every other feature: the people who never open it never download it.
 *
 * <p>**Listed inside the `<ProductRoute />` group in `app/router`**, not beside it. That
 * guard is applied by placement — a screen put in the group is guarded without anybody
 * remembering — and `screenForPath` resolves `email_warmup` from `app/navigation/screens.ts`
 * with no edit. Listed before `placeholderRoutes` so it wins; that entry disappears on its
 * own once `ROUTES.emailWarmup` is in `BUILT_ROUTES`, which is the same line this change
 * touches.
 *
 * <p>The detail route is a **pattern**, filled in by `routeTo.emailWarmupAccount(id)`. It is
 * registered in `screens.ts` as well, so `ProductRoute` demands `email_warmup` in front of it
 * — which `screenForPath` could not have done before it learned to match a pattern. A mailbox
 * somebody may not list is a mailbox they may not open.
 */
// The operator's three catalogue screens, reached from the receivers screen. Each is its own
// chunk, and none is downloaded by anybody who is not the platform operator.
const TemplatesPage = lazy(() =>
  import('./routes/TemplatesPage').then((m) => ({ default: m.TemplatesPage })),
);
const TemplateCategoriesPage = lazy(() =>
  import('./routes/TemplateCategoriesPage').then((m) => ({ default: m.TemplateCategoriesPage })),
);
const ApiKeysPage = lazy(() =>
  import('./routes/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })),
);
const CreateApiKeyPage = lazy(() =>
  import('./routes/CreateApiKeyPage').then((m) => ({ default: m.CreateApiKeyPage })),
);

export const emailWarmupRoutes: RouteObject[] = [
  { path: ROUTES.emailWarmup, element: <EmailWarmupList /> },
  { path: ROUTES.emailWarmupBilling, element: <EmailWarmupBillingPage /> },
  { path: ROUTES.emailWarmupAccount, element: <EmailWarmupDetail /> },
  { path: ROUTES.emailWarmupCreate, element: <EmailWarmupCreate /> },
];

/**
 * The platform operator's Email Warmup screens: the receiver pool, and the templates, template
 * categories and API keys it opens.
 *
 * <p>Exported apart from {@link emailWarmupRoutes} because the router nests them under
 * `SuperAdminRoute` and that guard applies to the group, as it does for the catalogue's. A
 * screen added here inherits it by where it is put. The receivers belong to no customer, so
 * they are not behind the product guard that the mailbox screens are.
 */
export const emailWarmupPlatformRoutes: RouteObject[] = [
  { path: ROUTES.warmupReceivers, element: <ReceiversPage /> },
  { path: ROUTES.warmupTemplates, element: <TemplatesPage /> },
  { path: ROUTES.warmupTemplateCategories, element: <TemplateCategoriesPage /> },
  { path: ROUTES.warmupApiKeys, element: <ApiKeysPage /> },
  { path: ROUTES.warmupProviderConfig, element: <CreateApiKeyPage /> },
];

export { useMailAccounts } from './hooks/useMailAccounts';
export type { MailAccount } from './types';
export { warmupStatusLabel, warmupStatusTone } from './warmupStatus';
